<script lang="ts">
  import { _, locale } from "svelte-i18n";

  // Presentation only. The original registration form is supplied as a slot.
  export let pricingPlan: string;

  const translations = {
    cs: {
      eyebrow: "Zkušební verze",
      headingEnd: "30 dní zdarma.",
      lead: "Vytvořte si účet a vyzkoušejte správu obsahu a obrazovek v praxi.",
      facts: [
        ["30 dní od registrace", "Čas zjistit, jak Ki-Wi Signage zapadne do vašeho provozu."],
        ["Bez platební karty", "K registraci ji nepotřebujete."],
        ["Bez automatické fakturace", "Placené pokračování vyžaduje váš výslovný souhlas s cenou a podmínkami."],
      ],
      formTitle: "Vytvoření účtu",
      planLabel: "Zkoušíte",
      endHeading: "Co se stane po 30 dnech?",
      end: "Pokud nepotvrdíte placené pokračování, po 30 dnech se registrovaná zařízení deaktivují a nebude je možné ovládat v CMS.",
      help: "Potřebujete se nejdřív poradit?",
      helpLink: "Napište nám",
      contact: "https://www.kiwisignage.com/cs/kontakt",
    },
    en: {
      eyebrow: "Free trial",
      headingEnd: "Free for 30 days.",
      lead: "Create an account and try managing your content and screens in practice.",
      facts: [
        ["30 days from registration", "Time to see how Ki-Wi Signage fits your day-to-day needs."],
        ["No credit card required", "You do not need one to register."],
        ["No automatic billing", "Paid use requires your explicit agreement to the price and terms."],
      ],
      formTitle: "Create an account",
      planLabel: "Your trial",
      endHeading: "What happens after 30 days?",
      end: "If you do not confirm paid use, registered devices will be deactivated after 30 days and can no longer be controlled in the CMS.",
      help: "Want to talk it through first?",
      helpLink: "Contact us",
      contact: "https://www.kiwisignage.com/contact",
    },
  };

  $: localized = $locale === "cs" || $locale === "en";
  $: cs = $locale === "cs";
  $: copy = cs ? translations.cs : translations.en;
  $: plan = pricingPlan === "entry" ? "Entry" : "Premium";
</script>

<!-- The marketing-site container owns the header offset and viewport gutters.
     This registration composition intentionally aligns copy and form at the top. -->
<div class="trial" class:legacy={!localized}>
  <div class="trial-layout">
    <section class="trial-intro" aria-labelledby={localized ? "trial-title" : undefined}>
      {#if localized}
        <div class="heading-group">
          <p class="eyebrow">{copy.eyebrow}</p>
          <h1 class="kiwi-hero-title" id="trial-title">{cs ? "Vyzkoušejte " : ""}<span class="kiwi-name-nowrap">Ki-Wi Signage.</span><br />{copy.headingEnd}</h1>
        </div>
        <p class="trial-lead">{copy.lead}</p>
        <dl class="trial-facts">
          {#each copy.facts as [label, description]}
            <div><dt>{label}</dt><dd>{description}</dd></div>
          {/each}
        </dl>
      {:else}
        <div class="main-header">{$_(pricingPlan === "entry" ? "register.header_entry" : "register.header_premium")}</div>
        <p class="text-header">{$_("register.header_text")}</p>
      {/if}
    </section>
    <section class="trial-registration" aria-labelledby={localized ? "form-title" : undefined}>
      {#if localized}
        <div class="trial-form-heading">
          <h2 id="form-title">{copy.formTitle}</h2>
          <p>{copy.planLabel} <strong>{plan}</strong></p>
        </div>
      {/if}
      <slot />
    </section>
    {#if localized}
      <section class="trial-after" aria-labelledby="trial-end">
        <h2 id="trial-end">{copy.endHeading}</h2>
        <p>{copy.end}</p>
        <p class="trial-help">{copy.help}<br /><a href={copy.contact} target="_top"><span>{copy.helpLink}</span><svg class="kiwi-link-icon" data-direction="page" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M3 8h10m-4-4 4 4-4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg></a></p>
      </section>
    {/if}
  </div>
</div>

<style>
  /* Ki-Wi website tokens and the approved trial composition, 29 October 2026.
     Form controls and their styles remain owned by Register.svelte. */
  .trial {
    --kiwi-color-ink: #0b0b0b;
    --kiwi-color-muted: #566056;
    --kiwi-color-brand: #6aa942;
    --kiwi-color-brand-deep: #39772d;
    --kiwi-color-line: #dce5d8;
    color: var(--kiwi-color-ink);
    max-width: 1240px;
    margin-inline: auto;
    padding-bottom: 64px;
  }
  .trial-layout { display: grid; grid-template-columns: minmax(0, .85fr) minmax(0, 1.4fr); grid-template-rows: auto 1fr; gap: 34px 72px; align-items: start; }
  .trial-intro { grid-column: 1; grid-row: 1; }
  .heading-group { display: grid; gap: 14px; }
  .eyebrow { display: flex; align-items: center; gap: 12px; font-size: 12px; font-weight: 600; line-height: 16px; letter-spacing: .14em; text-transform: uppercase; }
  .eyebrow::before { content: ""; display: block; width: 32px; height: 2px; flex: none; background: var(--kiwi-color-brand); }
  h1 { font-size: 52px; line-height: 1.1; letter-spacing: -.04em; font-weight: 600; margin: 0; }
  .kiwi-name-nowrap { white-space: nowrap; }
  .trial-lead { font-size: 16px; line-height: 1.75; color: var(--kiwi-color-muted); margin-top: 24px; max-width: 39ch; }
  .trial-facts { margin-top: 30px; display: grid; gap: 18px; }
  .trial-facts > div { position: relative; padding-left: 19px; }
  .trial-facts > div::before { content: ""; position: absolute; left: 0; top: 7px; height: 9px; width: 3px; border-radius: 3px; background: var(--kiwi-color-brand); }
  dt { font-size: 14px; line-height: 1.5; font-weight: 600; }
  dd { font-size: 13px; line-height: 1.7; margin-top: 3px; color: var(--kiwi-color-muted); max-width: 43ch; }
  .trial-registration { grid-column: 2; grid-row: 1 / 3; min-width: 0; }
  .trial-form-heading { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 0 2px 17px; }
  .trial-form-heading h2 { font-size: 17px; line-height: 1.5; font-weight: 500; }
  .trial-form-heading p { font-size: 11px; color: var(--kiwi-color-muted); white-space: nowrap; }
  .trial-form-heading strong { font-weight: 500; color: var(--kiwi-color-brand-deep); }
  .trial:not(.legacy) :global(.form-wrapper) { border-radius: 18px; }
  .trial-after { grid-column: 1; grid-row: 2; border-top: 1px solid var(--kiwi-color-line); padding-top: 24px; }
  .trial-after h2 { font-size: 15px; line-height: 1.5; font-weight: 500; margin-bottom: 9px; }
  .trial-after > p { font-size: 13px; line-height: 1.8; color: var(--kiwi-color-muted); max-width: 48ch; }
  .trial-after .trial-help { margin-top: 22px; border-top: 1px solid var(--kiwi-color-line); padding-top: 22px; max-width: none; font-size: 12px; }
  .trial-help a { display: inline-flex; align-items: center; gap: 8px; font-weight: 500; padding-top: 6px; color: var(--kiwi-color-brand-deep); text-decoration: none; }
  .trial-help a:hover span { text-decoration: underline; text-decoration-thickness: 1px; text-underline-offset: 4px; }
  .trial-help a:focus-visible { outline: 3px solid var(--kiwi-color-brand-deep); outline-offset: 5px; border-radius: 2px; }
  .kiwi-link-icon { flex: none; }
  .legacy { max-width: none; padding-bottom: 0; }
  .legacy .trial-layout { display: block; }
  .main-header { font-size: 3rem; color: #000; line-height: 1.3em; text-align: center; margin-bottom: 1rem; font-weight: bold; }
  .text-header { font-size: 2rem; color: #000; text-align: center; white-space: pre-line; margin-bottom: 3rem; }
  @media (max-width: 1199px) and (min-width: 1100px) { .trial-layout { column-gap: 48px; } h1 { font-size: 48px; } }
  @media (max-width: 1099px) {
    .trial { max-width: 780px; padding-bottom: 48px; }
    .trial-layout { grid-template-columns: 1fr; row-gap: 32px; }
    .trial-intro, .trial-registration, .trial-after { grid-column: 1; grid-row: auto; }
    h1 { font-size: 46px; }
    .trial-lead { max-width: 55ch; margin-top: 20px; }
    .trial-facts { grid-template-columns: 1fr 1fr 1.3fr; gap: 22px; margin-top: 28px; }
    .trial-facts > div { padding-left: 0; }
    .trial-facts > div::before { display: none; }
    dt { font-size: 13px; } dd { font-size: 12px; }
    .trial-after > p { max-width: 75ch; }
    .legacy { max-width: none; padding-bottom: 0; }
  }
  @media (max-width: 699px) {
    .trial { padding-bottom: 40px; }
    .trial-layout { row-gap: 28px; }
    .heading-group { gap: 12px; }
    .eyebrow { font-size: 11px; gap: 10px; }
    .eyebrow::before { width: 24px; }
    h1 { font-size: 36px; line-height: 1.16; letter-spacing: -.035em; }
    .trial-lead { font-size: 14px; max-width: 42ch; margin-top: 18px; }
    .trial-facts { grid-template-columns: 1fr; gap: 13px; margin-top: 24px; }
    .trial-facts > div { padding-left: 17px; }
    .trial-facts > div::before { display: block; }
    dd { max-width: 46ch; }
    .trial-facts > div:nth-child(-n+2) dd { display: none; }
    .trial-form-heading { padding-bottom: 13px; }
    .trial-form-heading h2 { font-size: 16px; }
    .trial-form-heading p { font-size: 10px; }
    .trial:not(.legacy) :global(.form-wrapper) { border-radius: 14px; }
    .trial-after { padding-top: 22px; }
    .legacy { padding-bottom: 0; }
  }
  @media (max-width: 319px) { h1 { font-size: 31px; } .trial-form-heading { flex-wrap: wrap; gap: 4px; } }
  @media (max-width: 600px) {
    .main-header { font-size: 2rem; line-height: 1.3em; margin-bottom: 1rem; }
    .text-header { font-size: 1rem; margin-bottom: 1.5rem; }
  }
</style>
